import React from 'react';
import {
  createData,
  DashboardTableProps,
  Row,
} from 'components/Table/DashboardTable';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import { DocumentStatusMapper } from 'components/StatusMappers/DocumentStatusMapper';
import { formatDateStringTimeUTC } from 'functions/format/formatDate';
import { DocumentStatusEnum } from 'common/document/enums/document-status.enum';
import { ICandidatePageDocument } from 'common/document/interfaces/candidate-page-document';
import OutlinedIconButton from '../../../../components/Buttons/OutlinedIconButton';
import { ReactComponent as TrashIcon } from 'assets/images/trash-can.svg';
import { useManager } from '../../../../contexts/access/ManagerContext/useManager';

type InputProps = {
  items: ICandidatePageDocument[];
  onReview: (item: ICandidatePageDocument) => void;
  onManage: (item: ICandidatePageDocument) => void;
  onSendReminder: (item: ICandidatePageDocument) => void;
  onRemoveDocument: (item: ICandidatePageDocument) => void;
};

const documentStatusesForReminder = [
  DocumentStatusEnum.REQUESTED,
  DocumentStatusEnum.REJECTED,
  DocumentStatusEnum.PARTIALLY_UPLOADED,
];

export const CandidateDocumentToTableRow = ({
  items,
  onReview,
  onManage,
  onSendReminder,
  onRemoveDocument,
}: InputProps): DashboardTableProps => {
  const { isReadOnly } = useManager();

  const rows: Row[] = items.map((item) => ({
    id: item.candidateDocumentId,
    key: item.documentName,
    cells: createData(
      {
        key: `${item.documentName}title`,
        element: item.documentName,
        isBold: true,
        width: '27%',
      },
      {
        key: `${item.documentName}status`,
        element: <DocumentStatusMapper status={item.status} />,
        width: '17%',
      },
      {
        key: `${item.documentName}expiration`,
        element: item.expirationDate
          ? formatDateStringTimeUTC(item.expirationDate)
          : '-',
        width: '15%',
      },
      {
        key: `${item.documentName}actions`,
        width: '30%',
        element: (
          <>
            {!isReadOnly && item.status === DocumentStatusEnum.APPROVED && (
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  justifyContent: 'flex-end',
                }}
              >
                <ButtonWithArrowIcon
                  outlined={true}
                  size="small"
                  onClick={() => {
                    onManage(item);
                  }}
                >
                  Manage
                </ButtonWithArrowIcon>
                <OutlinedIconButton
                  size="small"
                  onClick={() => {
                    onRemoveDocument(item);
                  }}
                  sx={{
                    minWidth: '26px !important',
                  }}
                >
                  <TrashIcon />
                </OutlinedIconButton>
              </div>
            )}

            {!isReadOnly &&
              [
                DocumentStatusEnum.UPLOADED,
                DocumentStatusEnum.NEEDS_REVIEW,
              ].includes(item.status) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    justifyContent: 'flex-end',
                  }}
                >
                  <ButtonWithArrowIcon
                    size="small"
                    onClick={() => {
                      onReview(item);
                    }}
                  >
                    Review
                  </ButtonWithArrowIcon>
                  <OutlinedIconButton
                    size="small"
                    onClick={() => {
                      onRemoveDocument(item);
                    }}
                    sx={{
                      minWidth: '26px !important',
                    }}
                  >
                    <TrashIcon />
                  </OutlinedIconButton>
                </div>
              )}
            {!isReadOnly &&
              item.status &&
              documentStatusesForReminder.includes(item.status) && (
                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    justifyContent: 'flex-end',
                  }}
                >
                  <ButtonWithArrowIcon
                    size="small"
                    onClick={() => {
                      onSendReminder(item);
                    }}
                  >
                    Send Reminder
                  </ButtonWithArrowIcon>
                  <OutlinedIconButton
                    size="small"
                    onClick={() => {
                      onRemoveDocument(item);
                    }}
                    sx={{
                      minWidth: '26px !important',
                    }}
                  >
                    <TrashIcon />
                  </OutlinedIconButton>
                </div>
              )}

            {isReadOnly &&
              [
                DocumentStatusEnum.APPROVED,
                DocumentStatusEnum.UPLOADED,
                DocumentStatusEnum.NEEDS_REVIEW,
              ].includes(item.status) && (
                <div>
                  <ButtonWithArrowIcon
                    outlined={true}
                    size="small"
                    onClick={() => {
                      onManage(item);
                    }}
                  >
                    View Details
                  </ButtonWithArrowIcon>
                </div>
              )}
          </>
        ),
      }
    ),
  }));

  return {
    columns: [
      {
        title: 'Document Name',
      },
      {
        title: 'Status',
      },
      {
        title: 'Exp.',
      },
      {
        title: '',
      },
    ],
    rows: rows,
  };
};
